import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth.jsx'
import { forgotPassword, confirmForgotPassword } from '../lib/cognito.js'
import AuthShell, { AuthField, AuthPasswordField, AuthButton, AuthError } from '../components/AuthShell.jsx'

// Two-phase Cognito reset: request a code, then set a new password with it.
// In demo mode (Cognito not configured) we just show a friendly explanation.
export default function ForgotPassword() {
  const { isCognito } = useAuth()
  const navigate = useNavigate()
  const [phase, setPhase] = useState('request') // request | reset | done
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function handleRequest(e) {
    e.preventDefault()
    setError('')
    if (!email.trim()) {
      setError('Enter the email on your account.')
      return
    }
    if (!isCognito) {
      // No backend in demo mode — explain and send them back to sign in.
      setPhase('reset')
      return
    }
    setBusy(true)
    try {
      await forgotPassword(email.trim())
      setPhase('reset')
    } catch (err) {
      setError(err.message || 'Could not start the reset.')
    } finally {
      setBusy(false)
    }
  }

  async function handleReset(e) {
    e.preventDefault()
    setError('')
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (!isCognito) {
      setPhase('done')
      return
    }
    if (!code.trim()) {
      setError('Enter the code from the email we sent.')
      return
    }
    setBusy(true)
    try {
      await confirmForgotPassword(email.trim(), code, newPassword)
      setPhase('done')
    } catch (err) {
      setError(err.message || 'Could not reset your password.')
    } finally {
      setBusy(false)
    }
  }

  if (phase === 'reset') {
    return (
      <AuthShell
        title="Reset password"
        subline={
          isCognito ? <>we emailed a code to {email}</> : <>demo mode — set any new password</>
        }
      >
        <form onSubmit={handleReset} className="flex flex-col gap-y-3">
          {isCognito && (
            <AuthField
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="6-digit code"
              autoComplete="one-time-code"
              autoFocus
            />
          )}
          <AuthPasswordField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
          <AuthError>{error}</AuthError>
          <AuthButton disabled={busy}>{busy ? 'Saving…' : 'Enter'}</AuthButton>
        </form>
      </AuthShell>
    )
  }

  if (phase === 'done') {
    return (
      <AuthShell title="Password reset" subline={<>you can now sign in with your new password</>}>
        <AuthButton type="button" onClick={() => navigate('/login')}>
          Back to sign in
        </AuthButton>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Reset password"
      subline={
        <>
          or <Link to="/login">back to sign in</Link>
        </>
      }
    >
      <form onSubmit={handleRequest} className="flex flex-col gap-y-3">
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
          autoFocus
        />
        <AuthError>{error}</AuthError>
        <AuthButton disabled={busy}>{busy ? 'Sending…' : 'Send reset code'}</AuthButton>
      </form>
    </AuthShell>
  )
}
